import { useState } from 'react';

interface GeminiModel {
  name: string;
  inputPer1M: number;
  outputPer1M: number;
  notes?: string;
  inUse?: boolean;
}

interface OpenAIModel {
  name: string;
  inputPer1M: number;
  outputPer1M: number;
  cachedInputPer1M: number;
  notes?: string;
}

const GEMINI_MODELS: GeminiModel[] = [
  { name: 'gemini-2.5-flash-lite', inputPer1M: 0.10, outputPer1M: 0.40, notes: 'Used in app (prompt)', inUse: true },
  { name: 'gemini-3.1-flash-lite-image', inputPer1M: 0.25, outputPer1M: 30.00, notes: 'Used in app (image gen) — "Nano Banana 2 Lite"', inUse: true },
  { name: 'gemini-2.5-flash-image', inputPer1M: 0.30, outputPer1M: 30.00, notes: 'Legacy "Nano Banana" — replaced 2026-07' },
  { name: 'gemini-3.1-flash-image', inputPer1M: 0.50, outputPer1M: 60.00, notes: '"Nano Banana 2" — next upgrade step if quality still insufficient' },
  { name: 'gemini-3-pro-image', inputPer1M: 2.00, outputPer1M: 120.00, notes: '"Nano Banana Pro" — best quality, ~4x cost' },
  { name: 'gemini-2.5-flash', inputPer1M: 0.30, outputPer1M: 2.50 },
  { name: 'gemini-2.5-pro', inputPer1M: 1.25, outputPer1M: 10.00, notes: '≤200k ctx' },
  { name: 'gemini-2.0-flash-preview-image-generation', inputPer1M: 0.10, outputPer1M: 0.40 },
  { name: 'gemini-3.1-flash-lite', inputPer1M: 0.25, outputPer1M: 1.50 },
  { name: 'gemini-3.1-pro-preview', inputPer1M: 2.00, outputPer1M: 12.00 },
];

const OPENAI_MODELS: OpenAIModel[] = [
  { name: 'gpt-4o', inputPer1M: 2.50, outputPer1M: 10.00, cachedInputPer1M: 1.25, notes: 'General purpose' },
  { name: 'gpt-4o-mini', inputPer1M: 0.15, outputPer1M: 0.60, cachedInputPer1M: 0.075, notes: 'Cost-efficient' },
  { name: 'gpt-4.1', inputPer1M: 2.00, outputPer1M: 8.00, cachedInputPer1M: 0.50, notes: 'Latest GPT-4' },
  { name: 'gpt-4.1-mini', inputPer1M: 0.40, outputPer1M: 1.60, cachedInputPer1M: 0.10 },
  { name: 'gpt-4.1-nano', inputPer1M: 0.10, outputPer1M: 0.40, cachedInputPer1M: 0.025, notes: 'Cheapest GPT-4' },
  { name: 'o3', inputPer1M: 10.00, outputPer1M: 40.00, cachedInputPer1M: 2.50, notes: 'Reasoning' },
  { name: 'o4-mini', inputPer1M: 1.10, outputPer1M: 4.40, cachedInputPer1M: 0.275, notes: 'Fast reasoning' },
];

interface ModelOption {
  label: string;
  inputPer1M: number;
  outputPer1M: number;
}

const ALL_MODELS: ModelOption[] = [
  ...GEMINI_MODELS.map(m => ({ label: `Gemini — ${m.name}`, inputPer1M: m.inputPer1M, outputPer1M: m.outputPer1M })),
  ...OPENAI_MODELS.map(m => ({ label: `OpenAI — ${m.name}`, inputPer1M: m.inputPer1M, outputPer1M: m.outputPer1M })),
];

function fmt(n: number) {
  return `$${n.toFixed(2)}`;
}

function AdminPricing() {
  const [promptTokens, setPromptTokens] = useState('');
  const [outputTokens, setOutputTokens] = useState('');
  const [selectedModelIdx, setSelectedModelIdx] = useState(0);

  const computedCost = (() => {
    const pt = Number(promptTokens) || 0;
    const ot = Number(outputTokens) || 0;
    const model = ALL_MODELS[selectedModelIdx];
    if (!model) return null;
    const cost = (pt / 1_000_000) * model.inputPer1M + (ot / 1_000_000) * model.outputPer1M;
    return cost;
  })();

  return (
    <div>
      <div className="admin-topbar">
        <h1>Pricing Reference</h1>
      </div>
      <div className="admin-content">

        {/* Section 1: Models in use */}
        <div className="admin-section-title">Models in Use</div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <div className="admin-stat-card admin-pricing-inuse" style={{ flex: '1 1 280px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Prompt Engineering
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary-dark)', marginBottom: '0.25rem' }}>
              gemini-2.5-flash-lite
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Text-only · $0.10/M input · $0.40/M output
            </div>
            <span className="admin-pill admin-pill-success" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
              In use
            </span>
          </div>
          <div className="admin-stat-card admin-pricing-inuse" style={{ flex: '1 1 280px' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Image Generation
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary-dark)', marginBottom: '0.25rem' }}>
              gemini-3.1-flash-lite-image
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Image output · $0.25/M input · $30.00/M output
            </div>
            <span className="admin-pill admin-pill-success" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
              In use
            </span>
          </div>
        </div>

        {/* Warning banner */}
        <div className="admin-pricing-warning">
          ⚠️ Internal <code>pricing.ts</code> rates may differ from current Google rates above. Update{' '}
          <code>backend/src/config/pricing.ts</code> to reflect current pricing.
        </div>

        {/* Section 2: Google Gemini Pricing */}
        <div className="admin-section-title">Google Gemini Pricing</div>
        <div className="admin-table-wrap" style={{ marginBottom: '1.5rem' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Input ($/1M tokens)</th>
                <th>Output ($/1M tokens)</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {GEMINI_MODELS.map(m => (
                <tr key={m.name} className={m.inUse ? 'admin-pricing-inuse' : ''}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{m.name}</span>
                      {m.inUse && (
                        <span className="admin-pill admin-pill-success">In use</span>
                      )}
                    </div>
                  </td>
                  <td>{fmt(m.inputPer1M)}</td>
                  <td>{fmt(m.outputPer1M)}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{m.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Section 3: OpenAI Comparison */}
        <div className="admin-section-title">OpenAI Comparison</div>
        <div className="admin-table-wrap" style={{ marginBottom: '0.75rem' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Input ($/1M tokens)</th>
                <th>Output ($/1M tokens)</th>
                <th>Cached Input</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {OPENAI_MODELS.map(m => (
                <tr key={m.name}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{m.name}</span>
                      <span className="admin-pill">OpenAI</span>
                    </div>
                  </td>
                  <td>{fmt(m.inputPer1M)}</td>
                  <td>{fmt(m.outputPer1M)}</td>
                  <td>{fmt(m.cachedInputPer1M)}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{m.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-faint)', marginBottom: '1.5rem' }}>
          Prices as of May 2026. Verify at{' '}
          <a href="https://platform.openai.com/pricing" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
            platform.openai.com/pricing
          </a>{' '}
          before use.
        </div>

        {/* Section 4: Cost estimator */}
        <div className="admin-section-title">Cost Estimator</div>
        <div className="admin-chart-card" style={{ maxWidth: 520 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                Model
              </label>
              <select
                className="admin-filter-select"
                style={{ width: '100%' }}
                value={selectedModelIdx}
                onChange={e => setSelectedModelIdx(Number(e.target.value))}
              >
                {ALL_MODELS.map((m, i) => (
                  <option key={m.label} value={i}>{m.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Prompt tokens
                </label>
                <input
                  type="number"
                  className="admin-filter-input"
                  style={{ width: '100%' }}
                  placeholder="e.g. 1000"
                  min={0}
                  value={promptTokens}
                  onChange={e => setPromptTokens(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Output tokens
                </label>
                <input
                  type="number"
                  className="admin-filter-input"
                  style={{ width: '100%' }}
                  placeholder="e.g. 500"
                  min={0}
                  value={outputTokens}
                  onChange={e => setOutputTokens(e.target.value)}
                />
              </div>
            </div>

            <div style={{
              background: 'var(--surface-alt)',
              borderRadius: 'var(--radius-sm)',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Estimated cost</span>
              <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-dark)' }}>
                {computedCost !== null && (promptTokens !== '' || outputTokens !== '')
                  ? `$${computedCost.toFixed(6)}`
                  : '—'}
              </span>
            </div>

            {computedCost !== null && (promptTokens !== '' || outputTokens !== '') && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
                {Number(promptTokens) || 0} prompt tokens × ${ALL_MODELS[selectedModelIdx].inputPer1M}/1M
                {' '}+ {Number(outputTokens) || 0} output tokens × ${ALL_MODELS[selectedModelIdx].outputPer1M}/1M
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminPricing;
